import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function EmpatheticLead() {
  return <section className="w-full bg-cream-dark py-20 px-6" data-id="element-48">
      <div className="max-w-reading mx-auto space-y-24" data-id="element-49">
        {/* The Lead */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="space-y-8 text-xl leading-relaxed text-charcoal" data-id="element-50">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-8" data-id="element-51">
            You're handling things.
          </h2>

          <p data-id="element-52">
            You're showing up. You're responding. You're keeping the wheels on.
          </p>

          <p className="font-medium" data-id="element-53">
            And yet—your body feels like it's braced all day.
          </p>

          <div className="bg-white/80 p-8 rounded-2xl border-l-4 border-teal my-8 shadow-sm" data-id="element-54">
            <p className="font-serif text-2xl italic text-charcoal-muted leading-relaxed" data-id="element-55">
              Jaw tight. Shoulders up. Chest wired. Brain sprinting.
            </p>
          </div>

          <p data-id="element-56">It's not always a panic attack. It's worse than that sometimes.</p>

          <p data-id="element-57">
            It's the constant low-level alarm that makes everything feel urgent…
            even when nothing is happening.
          </p>

          <p data-id="element-58">
            If you've tried "just relax," long meditations, perfect morning
            routines, or advice that assumes you have spare time—this will feel
            different.
          </p>

          <p className="font-semibold text-teal-dark" data-id="element-59">
            Because it's built for the days when you have no spare capacity.
          </p>
        </motion.div>

        {/* The Real Problem */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="space-y-8" data-id="element-60">
          <div className="space-y-4" data-id="element-61">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-charcoal" data-id="element-62">
              The real problem{' '}
              <span className="text-charcoal-muted font-normal italic text-2xl block mt-2" data-id="element-63">
                (and why it's not your personality)
              </span>
            </h2>
          </div>

          <p className="text-xl leading-relaxed" data-id="element-64">
            High-functioning anxiety doesn't look like falling apart. It looks
            like:
          </p>

          <ul className="space-y-4 pl-2" data-id="element-65">
            {['over-preparing', 'over-explaining', 'overthinking at night', 'snapping faster than you want', 'being productive… while secretly feeling one inch from the edge'].map((item, i) => <li key={i} className="flex items-start gap-3 text-lg text-charcoal" data-id="element-66">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0" data-id="element-67" />
                <span data-id="element-68">{item}</span>
              </li>)}
          </ul>

          <div className="space-y-2 text-xl font-medium pt-4" data-id="element-69">
            <p data-id="element-70">You're not weak.</p>
            <p data-id="element-71">You're not broken.</p>
            <p className="text-teal-dark font-bold text-2xl" data-id="element-72">
              You're overloaded.
            </p>
          </div>

          <div className="relative bg-white p-8 md:p-10 rounded-xl shadow-sm border border-amber/20 mt-8" data-id="element-73">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber to-teal" data-id="element-74" />
            <p className="text-lg text-charcoal-muted mb-4 uppercase tracking-widest text-xs font-bold" data-id="element-75">
              Your nervous system has learned a rule:
            </p>
            <blockquote className="font-serif text-2xl md:text-3xl italic text-charcoal leading-snug" data-id="element-76">
              "If I'm not on top of everything, something bad happens."
            </blockquote>
            <p className="mt-6 text-lg" data-id="element-77">
              So it stays "on." Even when you want to rest.
            </p>
          </div>
        </motion.div>

        {/* Why Advice Fails */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="space-y-10" data-id="element-78">
          <motion.h2 variants={fadeInUp} className="font-serif text-3xl md:text-4xl font-bold text-charcoal" data-id="element-79">
            Why most "calm" advice fails <br data-id="element-80" />
            <span className="text-teal italic" data-id="element-81">(especially for women 30+)</span>
          </motion.h2>

          <motion.p variants={fadeInUp} className="text-xl leading-relaxed" data-id="element-82">
            Most anxiety advice is built for people who have time, energy, and a
            quiet life. <br data-id="element-83" />
            <span className="font-bold" data-id="element-84">You don't.</span>
          </motion.p>

          <div className="grid gap-6" data-id="element-85">
            {[{
            advice: 'Meditate for 20 minutes.',
            reality: 'When? With what brain?'
          }, {
            advice: 'Do a full morning routine.',
            reality: 'So… add more work?'
          }, {
            advice: 'Think positive.',
            reality: 'Your body is still in alarm mode.'
          }, {
            advice: 'Take a bath.',
            reality: 'Your mind follows you into the water.'
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center gap-3 md:gap-6" data-id="element-86">
                <div className="flex-1" data-id="element-87">
                  <span className="text-charcoal-muted text-sm uppercase tracking-wider font-bold" data-id="element-88">
                    They say:
                  </span>
                  <p className="font-serif text-xl italic text-charcoal" data-id="element-89">
                    "{item.advice}"
                  </p>
                </div>
                <div className="hidden md:block w-px h-12 bg-gray-200" data-id="element-90" />
                <div className="flex-1" data-id="element-91">
                  <span className="text-teal text-sm uppercase tracking-wider font-bold" data-id="element-92">
                    Reality:
                  </span>
                  <p className="text-lg font-medium text-charcoal" data-id="element-93">
                    {item.reality}
                  </p>
                </div>
              </motion.div>)}
          </div>

          <motion.div variants={fadeInUp} className="pt-8 text-xl leading-relaxed border-t border-gray-200" data-id="element-94">
            <p className="mb-4" data-id="element-95">
              When you're overloaded, you don't need a bigger routine.
            </p>
            <p className="font-serif text-2xl italic text-teal-dark font-medium" data-id="element-96">
              You need a small, repeatable downshift that works even on your
              worst days.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}